import React, { useState } from 'react'
import { Box, Icon, Text, useDisclosure } from '@chakra-ui/react'
import { FaShareNodes } from 'react-icons/fa6'

import SharedButton from '~components/shared/buttons/shared-button'
import SharedModal from '~components/shared/modals/shared-modal'
import SharedInput from '~components/shared/form/shared-input'
import SharedLabel from '~components/shared/form/shared-label'
import SharedStack from '~components/shared/wrappers/shared-stack'
import InternationalPhoneInput from '~components/shared/form/intl-phone-input'
import { Country } from '~utils/constants/countries'
import { cleanPhoneNumber, validateEmail, validatePhone } from '~utils/helpers'
import { useClinic } from '~contexts/clinic-context'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'
import {
    PatientFieldsFragment,
    useShareVaccineRecordsMutation,
} from '~graphql/generated/graphql'

interface Props {
    patient: PatientFieldsFragment
}

/**
 * Lets clinic staff send a patient's vaccine records to whoever is asking (a
 * boarder, groomer, another clinic, the owner) by email and/or text. Sends the
 * same secure 7-day link the AI receptionist uses; the server authorizes the
 * request against the staff member's clinic membership.
 */
export default function ShareVaccinationRecordsButton({ patient }: Props) {
    const { clinic } = useClinic()
    const { isOpen, onOpen, onClose } = useDisclosure()
    const { onCompleted, onError } = useToastFeedback(
        `Sent ${patient.name || 'the patient'}'s vaccine records.`,
        'There was a problem sending those records. Please try again.',
    )

    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')

    const trimmedEmail = email.trim()
    const cleanedPhone = cleanPhoneNumber(phone)
    const emailValid = !trimmedEmail || Boolean(validateEmail(trimmedEmail))
    const phoneValid = !cleanedPhone || Boolean(validatePhone(cleanedPhone))
    const hasDestination = Boolean(trimmedEmail) || Boolean(cleanedPhone)
    const isConfirmDisabled = !hasDestination || !emailValid || !phoneValid

    const [shareVaccineRecords, { loading }] = useShareVaccineRecordsMutation({
        onCompleted: () => {
            onCompleted()
            handleClose()
        },
        onError,
    })

    function handleClose() {
        setEmail('')
        setPhone('')
        onClose()
    }

    function handleShare() {
        if (isConfirmDisabled) return
        shareVaccineRecords({
            variables: {
                input: {
                    patientId: patient.id,
                    email: trimmedEmail || undefined,
                    phone: cleanedPhone || undefined,
                },
            },
        })
    }

    return (
        <>
            <SharedButton
                colorScheme="brand"
                leftIcon={<Icon as={FaShareNodes} />}
                size="xs"
                variant="subtle"
                onClick={onOpen}
            >
                Share
            </SharedButton>
            <SharedModal
                title={`Share ${patient.name || 'patient'}'s vaccine records`}
                isOpen={isOpen}
                onClose={handleClose}
                onConfirm={handleShare}
                confirmText="Send"
                isConfirmDisabled={isConfirmDisabled}
                isLoading={loading}
                skipCloseOnConfirm
            >
                <SharedStack>
                    <Text fontSize="sm" color="gray.500">
                        Send a secure link to {patient.name || 'the patient'}
                        &apos;s vaccine records — for a boarder, groomer, another
                        clinic, or the owner. The link works for 7 days. Add an
                        email, a phone number, or both.
                    </Text>
                    <SharedInput
                        label="Email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="recipient@example.com"
                        error={
                            !emailValid ? 'Enter a valid email.' : undefined
                        }
                    />
                    <Box>
                        <SharedLabel mb={1}>Phone</SharedLabel>
                        <InternationalPhoneInput
                            value={phone}
                            setValue={(e) => setPhone(cleanPhoneNumber(e))}
                            placeholder="(555) 555-5555"
                            defaultCountry={
                                (clinic?.address?.country as Country) ||
                                'United States'
                            }
                        />
                        {!phoneValid && (
                            <Text mt={1} fontSize="xs" color="red.500">
                                Enter a valid phone number.
                            </Text>
                        )}
                    </Box>
                </SharedStack>
            </SharedModal>
        </>
    )
}
